import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { EnhancedBackground } from './components/EnhancedBackground';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { CPEListPage } from './pages/CPEListPage';
import { CPEFormPage } from './pages/CPEFormPage';
import { CPEDetailsPage } from './pages/CPEDetailsPage';
import { CPEMetricsPage } from './pages/CPEMetricsPage';
import { UserManagementPage } from './pages/UserManagementPage';
import ZoneListPage from './pages/ZoneListPage';
import ZoneFormPage from './pages/ZoneFormPage';
import ZoneDetailsPage from './pages/ZoneDetailsPage';
import MapPage from './pages/MapPage';
import SettingsPage from './pages/SettingsPage';

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <EnhancedBackground />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* CPE Routes */}
            <Route
              path="/map"
              element={
                <ProtectedRoute>
                  <MapPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cpes"
              element={
                <ProtectedRoute>
                  <CPEListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cpes/new"
              element={
                <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                  <CPEFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cpes/:id"
              element={
                <ProtectedRoute>
                  <CPEDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cpes/:id/edit"
              element={
                <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                  <CPEFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/metrics"
              element={
                <ProtectedRoute>
                  <CPEMetricsPage />
                </ProtectedRoute>
              }
            />

            {/* Zone Routes */}
            <Route
              path="/zones"
              element={
                <ProtectedRoute>
                  <ZoneListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zones/new"
              element={
                <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                  <ZoneFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zones/:id"
              element={
                <ProtectedRoute>
                  <ZoneDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zones/:id/edit"
              element={
                <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                  <ZoneFormPage />
                </ProtectedRoute>
              }
            />

            {/* User Management Routes (Super Admin only) */}
            <Route
              path="/users"
              element={
                <ProtectedRoute requiredRoles={['super_admin']}>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />

            {/* Settings (Admin and Super Admin) */}
            <Route
              path="/settings"
              element={
                <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to login */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Box>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;

